import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import SmoothScroll from "@/components/site/SmoothScroll";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import Enquiry from "@/components/site/Enquiry";
import { Reveal, FadeUp } from "@/components/site/Reveal";
import { CONTACT } from "@/data/content";
import { trackCall } from "@/lib/analytics";

export default function Contact() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const title = "Contact Perth Tiling WA | Free Quotes Across Perth";
  const description = "Get in touch with Perth Tiling WA for a free, detailed tiling quote. Call 0420 524 388, email admin@perthtilingwa.com.au or send an enquiry — servicing Perth's premier suburbs.";
  const keywords = "contact Perth Tiling WA, tiling quote Perth, tiler phone number Perth, free tiling quote";
  const breadcrumb = [
    { name: "Home", path: "/" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <SmoothScroll>
      <Seo pageKey="contact" overrideTitle={title} overrideDescription={description} overrideKeywords={keywords} breadcrumb={breadcrumb} />
      <div className="grain-overlay" aria-hidden />
      <div data-testid="contact-page" className="bg-[#f8fafc] min-h-screen">
        <Navbar />

        {/* Hero */}
        <section className="relative pt-40 pb-20 md:pt-52 md:pb-24 border-b border-[#0f172a]/10">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12">
            <nav className="mb-8 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#64748b]" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-[#9c7a4d] transition-colors">Home</Link>
              <span>/</span>
              <span className="text-[#0f172a]">Contact</span>
            </nav>
            <p className="overline !text-[#9c7a4d] mb-5">Contact</p>
            <h1 className="font-serif font-light text-[#0f172a] text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
              <Reveal load>Let's talk about</Reveal>
              <Reveal load delay={0.12}>your project.</Reveal>
            </h1>
            <FadeUp delay={0.2}>
              <p className="mt-8 max-w-2xl text-[#475569] text-lg md:text-xl font-light leading-relaxed">
                Every project starts with a conversation and a free site visit. Call, email or send an enquiry below — we'll get back to you promptly and arrange a time that suits.
              </p>
            </FadeUp>
          </div>
        </section>

        {/* Contact cards + map */}
        <section className="py-20 md:py-24">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-5 space-y-4">
              <a
                href={`tel:${CONTACT.phoneRaw}`}
                onClick={() => trackCall("contact_page")}
                data-testid="contact-page-phone"
                className="group flex items-start gap-5 border border-[#0f172a]/10 bg-white p-7 hover:border-[#9c7a4d] transition-colors"
              >
                <Phone className="h-5 w-5 text-[#9c7a4d] shrink-0 mt-1" />
                <div>
                  <span className="overline !text-[#94a3b8]">Call us</span>
                  <span className="block font-serif text-2xl md:text-3xl text-[#0f172a] group-hover:text-[#9c7a4d] mt-2 transition-colors">{CONTACT.phone}</span>
                  <span className="block text-[#64748b] font-light mt-1">Fastest way to reach us</span>
                </div>
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-page-email"
                className="group flex items-start gap-5 border border-[#0f172a]/10 bg-white p-7 hover:border-[#9c7a4d] transition-colors"
              >
                <Mail className="h-5 w-5 text-[#9c7a4d] shrink-0 mt-1" />
                <div>
                  <span className="overline !text-[#94a3b8]">Email us</span>
                  <span className="block font-serif text-xl md:text-2xl text-[#0f172a] group-hover:text-[#9c7a4d] mt-2 transition-colors break-all">{CONTACT.email}</span>
                  <span className="block text-[#64748b] font-light mt-1">Send plans, photos or questions</span>
                </div>
              </a>
              <div data-testid="contact-page-area" className="flex items-start gap-5 border border-[#0f172a]/10 bg-white p-7">
                <MapPin className="h-5 w-5 text-[#9c7a4d] shrink-0 mt-1" />
                <div>
                  <span className="overline !text-[#94a3b8]">Service area</span>
                  <span className="block font-serif text-xl md:text-2xl text-[#0f172a] mt-2">{CONTACT.area}</span>
                  <span className="block text-[#64748b] font-light mt-1">
                    From <Link to="/tiling/joondalup" className="underline decoration-[#9c7a4d]/40 hover:text-[#9c7a4d] transition-colors">Joondalup</Link> to{" "}
                    <Link to="/tiling/fremantle" className="underline decoration-[#9c7a4d]/40 hover:text-[#9c7a4d] transition-colors">Fremantle</Link> —{" "}
                    <Link to="/areas" className="underline decoration-[#9c7a4d]/40 hover:text-[#9c7a4d] transition-colors">see all areas</Link>
                  </span>
                </div>
              </div>
              <div data-testid="contact-page-hours" className="flex items-start gap-5 border border-[#0f172a]/10 bg-white p-7">
                <Clock className="h-5 w-5 text-[#9c7a4d] shrink-0 mt-1" />
                <div>
                  <span className="overline !text-[#94a3b8]">Hours</span>
                  <span className="block font-serif text-xl md:text-2xl text-[#0f172a] mt-2">Mon – Fri, 7am – 5pm</span>
                  <span className="block text-[#64748b] font-light mt-1">Saturday 8am – 1pm · Enquiries anytime</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <FadeUp>
                <div data-testid="contact-map" className="relative h-full min-h-[420px] border border-[#0f172a]/10 overflow-hidden bg-[#f1f5f9]">
                  <iframe
                    title="Perth Tiling WA service area — Perth, Western Australia"
                    src="https://www.google.com/maps?q=Perth,+Western+Australia&z=10&output=embed"
                    className="absolute inset-0 h-full w-full"
                    style={{ border: 0, filter: "grayscale(0.35) sepia(0.12)" }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
              </FadeUp>
            </div>
          </div>
        </section>

        {/* Enquiry form */}
        <Enquiry />

        <Footer />
      </div>
    </SmoothScroll>
  );
}
